import React, { useEffect, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { Greyscale, SearchIcon } from 'glints-aries';
import { isEmpty, isNil } from 'lodash';
import { defineMessage, useIntl } from 'react-intl';
import { useDebounce } from 'use-debounce';

import pushGTMEvent from 'src/common/utils/google-tag-manager';
import { apolloContextWithApiV2AlcClientName } from 'src/modules/ApolloWrapper/constants';
import { queryGetJobKeywordSuggestionsV2 } from 'src/modules/Opportunities/pages/Explore/Components/SearchSection/graphql';
import * as S from 'src/modules/Opportunities/pages/Explore/Components/SearchSection/SearchField.sc';

import { KEYS } from './utils';

export interface Props {
  keyword: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeywordClick: (value: string) => void;
  suggestionLimit: number;
  countryCode?: string;
  onEnter: (
    e:
      | React.KeyboardEvent<HTMLInputElement>
      | React.MouseEvent<HTMLLIElement, MouseEvent>,
    suggestions: string[],
    selectionCursor?: number
  ) => void;
  isHomepage?: boolean;
}

export const KeywordSearchField: React.FC<React.PropsWithChildren<Props>> = ({
  keyword,
  onChange,
  onKeywordClick,
  suggestionLimit,
  countryCode,
  onEnter,
  isHomepage = false,
}) => {
  const intl = useIntl();
  const [openSuggestions, setOpenSuggestions] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [selectionCursor, setSelectionCursor] = useState<number>(null);
  const apolloClient = useApolloClient();
  const [debouncedKeyword] = useDebounce(keyword, 300);

  useEffect(() => {
    const fetchJobTitleSuggestions = async () => {
      try {
        const { data } = await apolloClient.query({
          query: queryGetJobKeywordSuggestionsV2,
          variables: {
            keyword: debouncedKeyword || '',
            ...(countryCode ? { countryCode } : null),
            limit: suggestionLimit,
          },
          context: apolloContextWithApiV2AlcClientName,
        });
        setSuggestions(data.getJobTitleSuggestions?.jobTitles ?? []);
        if (
          !isEmpty(data.getJobTitleSuggestions?.jobTitles) &&
          openSuggestions
        ) {
          pushGTMEvent('glints/GoogleTagManager/KEYWORD_SUGGESTION_BOX_SHOWN', {
            payload: {
              keyword: debouncedKeyword,
              keyword_len: debouncedKeyword.length,
              experimentationInfo: data.getJobTitleSuggestions?.expInfo,
            },
          });
        }
      } catch (error) {
        throw error;
      }
    };
    fetchJobTitleSuggestions();
  }, [
    countryCode,
    debouncedKeyword,
    suggestionLimit,
    apolloClient,
    openSuggestions,
  ]);

  const onFocus = () => setOpenSuggestions(true);
  const onBlur = () => setOpenSuggestions(false);
  const onMouseOver = (index: number) => setSelectionCursor(index);

  const onArrowDown = () => {
    let newCursor;
    if (isNil(selectionCursor)) {
      newCursor = 0;
    } else if (selectionCursor === suggestions.length - 1) {
      newCursor = selectionCursor;
    } else {
      newCursor = selectionCursor + 1;
    }
    setSelectionCursor(newCursor);
  };
  const onArrowUp = () => {
    const newCursor =
      selectionCursor > 0 ? selectionCursor - 1 : selectionCursor;
    setSelectionCursor(newCursor);
  };

  const keyMaps = {
    [KEYS.ENTER]: onEnter,
    [KEYS.ARROW_DOWN]: onArrowDown,
    [KEYS.ARROW_UP]: onArrowUp,
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const decidedKeys = Object.values(KEYS);

    if (decidedKeys.includes(e.key)) {
      keyMaps[e.key](e, suggestions, selectionCursor);
      if (e.key === KEYS.ENTER) {
        setSelectionCursor(null);
      }
    }
  };

  const onSuggestionClick = (suggestion: string) => {
    pushGTMEvent('glints/GoogleTagManager/KEYWORD_SUGGESTION_BOX_CLICKED', {
      payload: { keyword: suggestion },
    });
    onKeywordClick(suggestion);
    setOpenSuggestions(false);
  };

  return (
    <>
      <S.TextField
        type="text"
        label={intl.formatMessage(
          defineMessage({
            id: 'job_explore_page.search_for_jobs',
            defaultMessage: 'Search for jobs',
          })
        )}
        data-cy="search_bar_job_title"
        removeFloatingLabel={true}
        allowClear={true}
        value={keyword}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
        startIcon={<SearchIcon />}
        isHomepage={isHomepage}
      />
      {openSuggestions && !isEmpty(suggestions) && (
        <S.SuggestionDropdownContainer>
          <S.SuggestionDropdown data-testid="jobs-search-keyword-suggestion-dropdown">
            {suggestions.map((suggestion: string, index: number) => (
              <S.SearchItemWrapper
                key={suggestion}
                className={index === selectionCursor && 'highlight'}
                onClick={() => onSuggestionClick(suggestion)}
                // to prevent the blur call after mouse down. [https://stackoverflow.com/questions/17769005/onclick-and-onblur-ordering-issue]
                onMouseDown={(e) => e.preventDefault()}
                onMouseOver={() => onMouseOver(index)}
              >
                <SearchIcon width="12px" height="12px" color={Greyscale.grey} />
                <S.SuggestionItem>{suggestion}</S.SuggestionItem>
              </S.SearchItemWrapper>
            ))}
          </S.SuggestionDropdown>
        </S.SuggestionDropdownContainer>
      )}
    </>
  );
};
